import { useEffect, useState } from 'react';
import { getAllOrganizations, getUnresolvedSosEvents, resolveSosEvent, SosEvent } from '../services/admin';
import { describeAlarmPositionDa } from './alarmPosition';

const POLL_INTERVAL_MS = 8000;

/** Dispatcher-facing emergency alert banner - polls GET /api/inrico/sos/events across every
 * organization for unresolved events (manual SOS and automatic fall detection both flow through
 * this same table, distinguished by sos_type - see documentation/protocol.md "Fall detection")
 * and shows the most recent one prominently at the top of every page. Real backend data, not a
 * mock - this is the "dispatcher-alarm" surface for automatic fall-detection alerts. Includes a
 * central "Afstil alarm" (Resolve) action (see documentation/protocol.md "Fall detection / Man
 * Down") - pushes a fall_alarm_resolved WebSocket message to the reporting radio. */
export default function FallAlarmBanner() {
  const [events, setEvents] = useState<SosEvent[]>([]);
  const [resolving, setResolving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const orgs = await getAllOrganizations();
        const results = await Promise.all(
          orgs.map((org) => getUnresolvedSosEvents(org.id).catch(() => [] as SosEvent[]))
        );
        if (!cancelled) {
          const all = results.flat().sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
          setEvents(all);
        }
      } catch {
        // Best-effort - a failed poll just leaves the previous (or empty) state until the next tick.
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (events.length === 0) return null;

  const latest = events[0];
  const isFall = latest.sos_type === 'fall_detected';
  const radioLabel = latest.human_radio_id || latest.radio_name || latest.inrico_radio_id;
  const time = new Date(latest.created_at).toLocaleTimeString();

  const handleResolve = async () => {
    setResolving(true);
    try {
      await resolveSosEvent(latest.org_id, latest.id);
      setEvents((prev) => prev.filter((e) => e.id !== latest.id));
      setConfirming(false);
    } catch {
      // Best-effort - the banner keeps showing the event; the next poll will retry naturally.
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-center gap-3 font-semibold animate-pulse">
      <span className="text-lg">{isFall ? '🚨 FALD DETEKTERET' : '🚨 SOS'}</span>
      <span>{radioLabel}</span>
      <span className="opacity-80 font-normal">kl. {time}</span>
      <span className="opacity-90 font-normal text-sm" data-alarm-position>{describeAlarmPositionDa(latest)}</span>
      <span className="opacity-60 font-normal text-xs">#{latest.id.slice(0, 8)}</span>
      {events.length > 1 && (
        <span className="opacity-80 font-normal">(+{events.length - 1} flere ubekræftede alarmer)</span>
      )}
      {confirming ? (
        <span className="flex items-center gap-2">
          <span className="text-sm font-normal">Afstil denne alarm?</span>
          <button
            onClick={handleResolve}
            disabled={resolving}
            className="bg-white text-red-700 text-sm font-semibold py-1 px-2 rounded disabled:opacity-50"
          >
            Ja, afstil
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={resolving}
            className="text-sm font-normal underline disabled:opacity-50"
          >
            Fortryd
          </button>
        </span>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="bg-white text-red-700 text-sm font-semibold py-1 px-2 rounded"
        >
          Afstil alarm
        </button>
      )}
    </div>
  );
}
